import { use, useCallback } from 'react';
import { Category } from 'shared/types/product';
import { ProductQuery } from 'shared/types/query/ProductQuery';
import { ProductPaginatedResult } from 'shared/types/result/PaginatedResult';
import useSWR from 'swr';
import { sdk } from 'sdk';
import { Product, Variant } from 'types/entity/product';

const useProduct = ({ query, limit }: Partial<ProductQuery> = {}) => {
  const { data: productsData } = useSWR(['/action/product/query', query, limit], () =>
    sdk.composableCommerce.product.query({ query, limit }, { skipQueue: true }),
  );

  const products = (productsData?.isError ? [] : (productsData?.data?.items as Product[])) ?? [];

  const { data: categoriesData } = useSWR('/action/product/queryCategories', () =>
    sdk.callAction<ProductPaginatedResult>({
      actionName: 'product/queryCategories',
      query: { format: 'tree' },
      skipQueue: true,
    }),
  );

  const categories = (categoriesData?.isError ? [] : (categoriesData?.data?.items as Category[])) ?? [];

  const queryProducts = useCallback(async (productQuery: ProductQuery) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.query(productQuery, { skipQueue: true });

    return res;
  }, []);

  return { products, categories, query: queryProducts };
};

export const useReferencedProducts = () => {
  const referencedProducts = useCallback(async (variant: Variant) => {
    const referenceIds = Object.entries(variant?.attributes as Record<string, any>)
      .filter(([key, value]) => key.endsWith('-reference') && typeof value === 'string' && value.trim())
      .map(([, value]) => value as string);
    if (referenceIds.length) {
      const response = await sdk.composableCommerce.product.query({ productIds: referenceIds }, { skipQueue: true });
      return response;
    }
    return {
      isError: false,
      data: {
        total: 0,
        items: [],
      },
    };
  }, []);

  return { referencedProducts };
};

export default useProduct;
